import { InfoOutlineIcon } from '@carboncopyco/icons';
import type {
  IntegrationVendor,
  ShareSecurityLevel,
} from '@carboncopyco/types';
import {
  Checkbox,
  ColorHex,
  MessageBox,
  TextBase,
  TextCaption,
  Tooltip,
} from '@carboncopyco/ui';
import { capitalize } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { Field, type FieldProps } from 'formik';
import { useRef } from 'react';
import { useIntegration } from '../Integrations';

type IntegrationLeadsFieldProps = {
  vendor: IntegrationVendor;
  securityLevel: ShareSecurityLevel;
  infoText: string;
};

const fieldNameByVendor: Record<
  Extract<IntegrationVendor, 'salesforce' | 'hubspot'>,
  string
> = {
  salesforce: 'salesforceLeads',
  hubspot: 'hubspotLeads',
};

export const IntegrationLeadsField = ({
  vendor,
  securityLevel,
  infoText,
}: IntegrationLeadsFieldProps) => {
  const infoRef = useRef(null);
  const { renewalRequired, integrationId, paused } = useIntegration({
    integrationType: vendor,
  });
  const [showRenewalMessage, setShowRenewalMessage] = useBoolean(true);

  if (securityLevel === 'internal' || !integrationId || paused) {
    return null;
  }

  if (renewalRequired) {
    if (showRenewalMessage) {
      return (
        <div className="pt-3 pb-4">
          <MessageBox
            danger
            close
            handleClose={setShowRenewalMessage.setFalse}
            label="One or more of your integrations failed. Contact the admin to reconnect it."
          />
        </div>
      );
    }
    return null;
  }

  return (
    <Field
      type="checkbox"
      name={fieldNameByVendor[vendor]}
    >
      {({ field: { checked, ...rest }, form }: FieldProps) => (
        <div className="flex items-center p-2 gap-1">
          <Checkbox
            {...rest}
            checked={form.values[fieldNameByVendor[vendor]]}
            onChange={() => {
              form.setFieldValue(
                fieldNameByVendor[vendor],
                !form.values[fieldNameByVendor[vendor]]
              );
            }}
            label={
              <TextBase
                color="text-ds-natural-600"
                label={`Add new leads to ${capitalize(vendor)}`}
              />
            }
          />
          <div ref={infoRef}>
            <InfoOutlineIcon
              size={16}
              color={ColorHex.natural}
            />
          </div>
          <Tooltip
            placement="top"
            trigger="mouseenter"
            hideOnClick="toggle"
            anchorRef={infoRef}
            noArrow
          >
            <div className="w-ds-64 py-2 px-2.5">
              <TextCaption label={infoText} />
            </div>
          </Tooltip>
        </div>
      )}
    </Field>
  );
};
